import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link as RouterLink } from "react-router-dom";
import {
  Box, Button, Paper, TextField, Typography, Alert, Stack, Link,
  InputAdornment, CircularProgress,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LockResetIcon from "@mui/icons-material/LockReset";
import { resetPassword } from "../data/api";
import { BRAND } from "../theme";

// Mirrors the backend policy: >= 8 chars with at least one letter and one digit.
const policyOk = (v: string) =>
  v.length >= 8 && /[A-Za-z]/.test(v) && /\d/.test(v);

const ResetPasswordPage: React.FC = () => {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!policyOk(pw)) { setError("Password must be at least 8 characters and include a letter and a number."); return; }
    if (pw !== confirm) { setError("Passwords do not match."); return; }
    setError("");
    setLoading(true);
    try {
      await resetPassword(token, pw);
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true, state: { registered: false } }), 1800);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.response?.data?.error || "This reset link is invalid or has expired. Please request a new one.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", p: 2,
      background: `linear-gradient(135deg, ${BRAND.greenDark} 0%, ${BRAND.green} 100%)` }}>
      <Paper elevation={0} sx={{ width: "100%", maxWidth: 420, p: { xs: 3, sm: 4 }, borderRadius: 3 }}>
        <Stack spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
          <Box sx={{ bgcolor: BRAND.greenSoft, color: BRAND.green, width: 56, height: 56, borderRadius: 2, display: "grid", placeItems: "center" }}>
            <LockResetIcon fontSize="large" />
          </Box>
          <Typography variant="h5">Choose a new password</Typography>
        </Stack>

        {!token && <Alert severity="error" sx={{ mb: 2 }}>Missing or invalid reset link.</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {done && <Alert severity="success" sx={{ mb: 2 }}>Your password has been reset. Redirecting to sign in…</Alert>}

        {!done && (
          <Box component="form" onSubmit={handleSubmit}>
            <TextField fullWidth label="New password" type="password" margin="normal" value={pw} required disabled={!token}
              onChange={(e) => setPw(e.target.value)} helperText="At least 8 characters, with a letter and a number"
              InputProps={{ startAdornment: <InputAdornment position="start"><LockOutlinedIcon fontSize="small" /></InputAdornment> }} />
            <TextField fullWidth label="Confirm new password" type="password" margin="normal" value={confirm} required disabled={!token}
              onChange={(e) => setConfirm(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><LockOutlinedIcon fontSize="small" /></InputAdornment> }} />
            <Button type="submit" fullWidth variant="contained" size="large" disabled={loading || !token} sx={{ mt: 2.5, py: 1.2 }}>
              {loading ? <CircularProgress size={22} color="inherit" /> : "Reset password"}
            </Button>
          </Box>
        )}

        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
          <Link component={RouterLink} to="/login" underline="hover" sx={{ fontWeight: 600 }}>Back to sign in</Link>
        </Typography>
      </Paper>
    </Box>
  );
};

export default ResetPasswordPage;
